/**
 * Feature access constants for role-based permissions
 * Use these constants when checking feature access with canAccessFeature()
 */

// Designer Features
export const DESIGNER_FEATURES = {
  HDG_SEAL: 'hdg-seal',
  REGIONAL_MARKETING: 'regional-marketing',
  SMART_MATCHING: 'smart-matching',
  MATCHING_DASHBOARD: 'matching-dashboard',
  DESIGNER_FILE_PROFIT: 'designer-file-profit',
  HOMEOWNER_FINANCING: 'homeowner-financing',
  DESIGNER_REFERRAL: 'designer-referral',
  GUILD_IQ: 'guild-iq',
} as const;

// Homeowner Features
export const HOMEOWNER_FEATURES = {
  LIMITED_GALLERY: 'limited-gallery',
  LIMITED_ARTICLES: 'limited-articles',
  CURATED_GALLERY: 'curated-gallery',
  GUILD_ARTICLES: 'guild-articles',
  POWERFUL_FILTERS: 'powerful-filters',
  IDEA_BOOKS: 'idea-books',
  SMART_GUILD_MATCHING: 'smart-guild-matching',
  EDUCATION: 'education',
} as const;

// All features combined
export const FEATURES = {
  ...DESIGNER_FEATURES,
  ...HOMEOWNER_FEATURES,
} as const;

// Feature descriptions for UI
export const FEATURE_DESCRIPTIONS = {
  // Designer
  [DESIGNER_FEATURES.HDG_SEAL]: 'HDG Seal of Approval certification',
  [DESIGNER_FEATURES.REGIONAL_MARKETING]: 'Regional advertising and marketing to homeowners',
  [DESIGNER_FEATURES.SMART_MATCHING]: 'Smart-Guild Matching algorithm',
  [DESIGNER_FEATURES.MATCHING_DASHBOARD]: 'Designer Hub matching dashboard',
  [DESIGNER_FEATURES.DESIGNER_FILE_PROFIT]: 'Earn revenue from designer files',
  [DESIGNER_FEATURES.HOMEOWNER_FINANCING]: 'Offer financing options to homeowners',
  [DESIGNER_FEATURES.DESIGNER_REFERRAL]: 'Designer referral program',
  [DESIGNER_FEATURES.GUILD_IQ]: 'AI-powered design intelligence and insights',

  // Homeowner
  [HOMEOWNER_FEATURES.LIMITED_GALLERY]: 'Browse limited project gallery',
  [HOMEOWNER_FEATURES.LIMITED_ARTICLES]: 'Access limited Guild Guide articles',
  [HOMEOWNER_FEATURES.CURATED_GALLERY]: 'Full curated project gallery access',
  [HOMEOWNER_FEATURES.GUILD_ARTICLES]: 'Full Guild Guide articles access',
  [HOMEOWNER_FEATURES.POWERFUL_FILTERS]: 'Advanced search filters',
  [HOMEOWNER_FEATURES.IDEA_BOOKS]: 'Create and save to Idea Books',
  [HOMEOWNER_FEATURES.SMART_GUILD_MATCHING]: 'Smart-Guild Matching with designers',
  [HOMEOWNER_FEATURES.EDUCATION]: 'Access to educational content',
} as const;

// Feature tier requirements
export const FEATURE_TIER_REQUIREMENTS = {
  // Designer tiers
  Home: [
    DESIGNER_FEATURES.HDG_SEAL,
    DESIGNER_FEATURES.REGIONAL_MARKETING,
    DESIGNER_FEATURES.SMART_MATCHING,
    DESIGNER_FEATURES.MATCHING_DASHBOARD,
  ],
  Design: [
    ...['Home'],
    DESIGNER_FEATURES.DESIGNER_FILE_PROFIT,
  ],
  'Design+': [
    ...['Design'],
    DESIGNER_FEATURES.HOMEOWNER_FINANCING,
    DESIGNER_FEATURES.DESIGNER_REFERRAL,
    DESIGNER_FEATURES.GUILD_IQ,
  ],

  // Homeowner tiers
  Basic: [
    HOMEOWNER_FEATURES.LIMITED_GALLERY,
    HOMEOWNER_FEATURES.LIMITED_ARTICLES,
  ],
  Guild: [
    HOMEOWNER_FEATURES.LIMITED_GALLERY,
    HOMEOWNER_FEATURES.LIMITED_ARTICLES,
    HOMEOWNER_FEATURES.CURATED_GALLERY,
    HOMEOWNER_FEATURES.GUILD_ARTICLES,
    HOMEOWNER_FEATURES.POWERFUL_FILTERS,
    HOMEOWNER_FEATURES.IDEA_BOOKS,
    HOMEOWNER_FEATURES.SMART_GUILD_MATCHING,
    HOMEOWNER_FEATURES.EDUCATION,
  ],
  'Guild+': [
    HOMEOWNER_FEATURES.LIMITED_GALLERY,
    HOMEOWNER_FEATURES.LIMITED_ARTICLES,
    HOMEOWNER_FEATURES.CURATED_GALLERY,
    HOMEOWNER_FEATURES.GUILD_ARTICLES,
    HOMEOWNER_FEATURES.POWERFUL_FILTERS,
    HOMEOWNER_FEATURES.IDEA_BOOKS,
    HOMEOWNER_FEATURES.SMART_GUILD_MATCHING,
    HOMEOWNER_FEATURES.EDUCATION,
  ],
} as const;

// Helper type for feature keys
export type FeatureKey = typeof FEATURES[keyof typeof FEATURES];
export type DesignerFeatureKey = typeof DESIGNER_FEATURES[keyof typeof DESIGNER_FEATURES];
export type HomeownerFeatureKey = typeof HOMEOWNER_FEATURES[keyof typeof HOMEOWNER_FEATURES];

